import React, { useEffect, useRef, useState } from 'react';
import { ClockIcon, RefreshIcon } from '@heroicons/react/outline';
import dataService, { type CreatorRefreshStatus } from '../../services/dataService';
import ResearchFreshness from './ResearchFreshness';

const pendingRequests = new Map<string, Promise<CreatorRefreshStatus>>();
function load(platform: string, handle: string) {
  const key = `${platform}/${handle}`;
  const pending = pendingRequests.get(key);
  if (pending) return pending;
  const request = dataService.getCreatorRefreshStatus(platform, handle).finally(() => pendingRequests.delete(key));
  pendingRequests.set(key, request);
  return request;
}

function dateLabel(value?: string | null) {
  if (!value || !Number.isFinite(Date.parse(value))) return 'Not checked yet';
  return new Date(value).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function RefreshDetails({ status }: { status: CreatorRefreshStatus }) {
  const waiting = status.status === 'waiting_batch';
  const checking = status.status === 'checking' || status.status === 'queued';
  const analyzing = status.status === 'analyzing';
  const trouble = ['partial', 'error', 'limited'].includes(status.status);
  const title = analyzing ? 'Analyzing new and changed posts' : checking ? 'Checking for new posts' : waiting ? `${status.pending_posts || 0} posts waiting for analysis`
    : trouble ? 'Research needs another look' : status.automatic ? 'Regular updates are on' : 'Saved research';
  return <div className="space-y-1" aria-live="polite">
    <p className="br-caption flex items-center gap-1.5" style={{ color: trouble ? 'var(--amber)' : 'var(--ink-2)', fontWeight: 700 }}>
      <ClockIcon className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />{title}
    </p>
    <p className="br-small">Last checked: {dateLabel(status.last_checked)}</p>
    {status.automatic && status.next_check && <p className="br-caption">Next check: {dateLabel(status.next_check)}</p>}
    {waiting && <p className="br-small">Batches normally run every {status.batch_window_hours || 6} hours, or sooner when full. Daily spending limits may delay the queue; results can take up to 24 hours after submission. Saved research stays available.</p>}
    {status.message && <p className="br-small">{status.message}</p>}
    {!!status.failed_posts && <p className="br-small">{status.failed_posts} post{status.failed_posts === 1 ? '' : 's'} could not be analyzed. This is not a “no sponsors” result.</p>}
    {!waiting && !checking && !analyzing && !trouble && status.automatic && <p className="br-caption">Follows this account’s posting cadence. Unchanged posts reuse saved analysis.</p>}
  </div>;
}

/** Shared per-account state, used by both public profiles and owner workspace.
 * Read-only polling never starts scraping; the explicit button owns that action. */
export default function PlatformRefresh({ platform, handle, lastScraped, busy = false, onCheck, onUpdated }: {
  platform: string; handle: string; lastScraped?: string | null; busy?: boolean; onCheck: () => void; onUpdated?: () => void;
}) {
  const [status, setStatus] = useState<CreatorRefreshStatus | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [epoch, setEpoch] = useState(0);
  const completed = useRef<string | null | undefined>();
  const updatedCallback = useRef(onUpdated);
  updatedCallback.current = onUpdated;
  useEffect(() => { completed.current = undefined; }, [platform, handle]);
  useEffect(() => {
    let live = true;
    setStatus(null);
    const read = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const result = await load(platform, handle);
        if (live) {
          const terminal = result.manual_status && !['queued', 'running'].includes(result.manual_status);
          const version = `${result.last_completed || ''}:${terminal ? `${result.manual_job_id}:${result.manual_status}` : ''}`;
          if (completed.current !== undefined && version !== completed.current && (result.last_completed || terminal)) updatedCallback.current?.();
          completed.current = version;
          setStatus(result); setUnavailable(false);
        }
      } catch {
        if (live) setUnavailable(true);
      }
    };
    void read();
    const timer = window.setInterval(read, 10_000);
    return () => { live = false; window.clearInterval(timer); };
  }, [platform, handle, busy, epoch]);
  const inFlight = busy || ['checking', 'queued', 'analyzing'].includes(status?.status || '');
  return <div className="flex flex-col gap-2 mt-auto">
    {status ? <RefreshDetails status={status} /> : <ResearchFreshness coverage={[{ platform, lastScraped }]} busy={busy} />}
    {unavailable && <p className="br-caption">Refresh status is unavailable. Your saved research is still here.</p>}
    <button type="button" disabled={inFlight} onClick={() => { onCheck(); setEpoch(e => e + 1); }}
      className="br-slab br-slab--cream br-slab--sm self-start" aria-label={`Check new posts for ${platform} @${handle}`}>
      <RefreshIcon className={`h-3.5 w-3.5 ${inFlight ? 'animate-spin' : ''}`} aria-hidden="true" />
      {inFlight ? status?.status === 'analyzing' ? 'Analyzing…' : 'Checking…' : 'Check for new posts'}
    </button>
  </div>;
}
